// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getWarehouse from "@bg/App/Handlers/PostalCodeHandler/getWarehouse";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { constants } from "@bg/XDL/constants";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { getLocalStorage, hasLocalStorage, parseBoolean, removeLocalStorage, setLocalStorage } from "@bg/XDL/Utilities";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import TextField from "@insite/mobius/TextField";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    newFulfillmentMethod: state.components.addressDrawer.fulfillmentMethod,
    isApplyingChanges: state.components.addressDrawer.isApplyingChanges,
    newPickUpWarehouse: state.components.addressDrawer.pickUpWarehouse,
    postalCodes: (state.xSystemList as any)["AcceptedDeliveryPostalCodes"]?.values,
});

const mapDispatchToProps = {
    getWarehouse,
};

type Props = ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> & {
        fulfillmentMethod: string;
    };

const PostalCode: React.FC<Props> = ({
    getWarehouse,
    fulfillmentMethod,
    newFulfillmentMethod,
    isApplyingChanges,
    newPickUpWarehouse,
    postalCodes,
}) => {
    const toaster = useToaster();

    const [fulfillment, setFulfillment] = React.useState(fulfillmentMethod);
    const [postalCode, setPostalCode] = React.useState(getLocalStorage(constants.localStorage.POSTAL_CODE) || "");
    const [error, setError] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const dispatch = useAppDispatch();

    // user signed out
    React.useEffect(() => {
        if (parseBoolean(getLocalStorage(constants.localStorage.SIGNED_OUT)) && postalCodes && postalCodes?.length) {
            if (fulfillment?.toLowerCase() === "ship") {
                if (postalCode && postalCode.length > 2 && checkIfNotValid(postalCode)) {
                    removeLocalStorage(constants.localStorage.SIGNED_OUT);
                    setPostalCode(postalCode || "");
                    clearStorage();
                } else {
                    setPostalCode(postalCode || "");
                    setError("");
                    getWarehouseData();
                }
            } else {
                setPostalCode(postalCode || "");
                setError("");
                getWarehouseData();
            }
        }
    }, [postalCodes]);

    // if the unauthenticated user changes between ship and pickup, make sure the postalCode is validated against the posta list again
    React.useEffect(() => {
        if (fulfillment?.toLowerCase() !== newFulfillmentMethod?.toLowerCase() && isApplyingChanges) {
            const postal = getLocalStorage(constants.localStorage.POSTAL_CODE);
            if (
                newFulfillmentMethod?.toLowerCase() !== "pickup" &&
                hasLocalStorage(constants.localStorage.POSTAL_CODE_ID) &&
                postal?.length
            ) {
                // from pickup to ship with invalid postal
                if (postal && postal.length > 2 && checkIfNotValid(postal)) {
                    setPostalCode(postal || "");
                    clearStorage();
                } else {
                    setPostalCode(postal || "");
                    setError("");
                    getWarehouseData();
                }
            }
        }
    }, [newFulfillmentMethod, isApplyingChanges]);

    // on pickup and chooses a location using fulfillment selector case
    React.useEffect(() => {
        const { POSTAL_CODE, POSTAL_CODE_ID } = constants.localStorage;
        const postal = getLocalStorage(POSTAL_CODE);
        const postalId = getLocalStorage(POSTAL_CODE_ID);
        if (
            newFulfillmentMethod?.toLowerCase() === "pickup" &&
            newPickUpWarehouse &&
            newPickUpWarehouse.postalCode !== postal &&
            isApplyingChanges
        ) {
            setLocalStorage(POSTAL_CODE_ID, newPickUpWarehouse.id);
            setLocalStorage(POSTAL_CODE, newPickUpWarehouse.postalCode);
        } else {
            setLocalStorage(POSTAL_CODE_ID, postalId || "");
            setLocalStorage(POSTAL_CODE, postal || "");
        }
    }, [isApplyingChanges]);

    const clearStorage = () => {
        const { POSTAL_CODE, POSTAL_CODE_ID } = constants.localStorage;
        setLocalStorage(POSTAL_CODE_ID, "");
        setLocalStorage(POSTAL_CODE, "");

        setError(`${translate("Unsupported Delivery Location")}`);
        toaster.addToast({
            body: (
                <Typography
                    css={css`
                        margin: 0;
                    `}
                >
                    {translate("Error while setting warehouse.")}
                </Typography>
            ),
            messageType: "warning",
        });
        dispatch({
            type: "commonActionSetter/SetAction",
            payload: {
                name: "resetShipToAddress",
                value: true,
            },
        });
    };

    const checkIfNotValid = (value: string) => {
        if (!postalCodes || !postalCodes.length) {
            return false;
        }
        return !postalCodes.filter(
            (zip: any) => zip.name?.toLowerCase().indexOf(value.substring(0, 3)?.toLowerCase()) === 0,
        ).length;
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            event.stopPropagation();
            if (event.currentTarget.value !== "") {
                setError("");
                (event.target as any).blur();
                getWarehouseData(true);
            } else {
                setError(`${translate("Postal code does not accept empty values")}`);
            }
        }
    };

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setError("");
        if (event.currentTarget.value === ".") {
            event.preventDefault();
            return;
        }
        const value = event.target.value;

        // check if at least the first 3 letters exist in any of the supported PostalCodes
        if (fulfillment?.toLowerCase() === "ship" && value.length > 2 && checkIfNotValid(value)) {
            setPostalCode(value);
            setError(`${translate("Unsupported Delivery Location")}`);
        } else {
            setPostalCode(value);
            setError("");
        }
    };

    const getWarehouseByValue = (postal: string, hasUserChangedPostal?: boolean) => {
        setIsLoading(true);
        getWarehouse({
            guestPostalCode: postal,
            hasUserChangedPostal,
            onSuccess: result => {
                setIsLoading(false);
                toaster.addToast({
                    body: (
                        <Typography
                            css={css`
                                margin: 0;
                            `}
                        >
                            {translate("Warehouse set to:")}&nbsp;
                            {result}
                        </Typography>
                    ),
                    messageType: "success",
                });
                dispatch({
                    type: "commonActionSetter/SetAction",
                    payload: {
                        name: "resetShipToAddress",
                        value: false,
                    },
                });
            },

            onError: error => {
                setIsLoading(false);
                toaster.addToast({
                    body: (
                        <Typography
                            css={css`
                                margin: 0;
                            `}
                        >
                            {translate("Error while setting warehouse.")}&nbsp;{error}
                        </Typography>
                    ),
                    messageType: "warning",
                });
            },
        });
    };

    const getWarehouseData = (userChange?: boolean) => {
        if (!postalCode) {
            setError(`${translate("Postal code does not accept empty values")}`);
        } else if (postalCode.length < 3) {
            setError(`${translate("At least 3 characters required to find a warehouse")}`);
        } else {
            setError("");
            getWarehouseByValue(postalCode, userChange);
        }
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <StyledWrapper
                css={css`
                    padding-bottom: 20px;
                `}
            >
                <Typography as="h4" variant="h5">
                    {siteMessage("Custom_PostalCodeHeader")}
                </Typography>
                <StyledWrapper
                    css={css`
                        margin-bottom: ${fontConfig.spacing.regular};
                    `}
                >
                    <Typography size={17} lineHeight="19px">
                        {siteMessage("Custom_PostalCodeBodyCopy")}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper>
                    <TextField
                        cssOverrides={{
                            formInputWrapper: css`
                                margin-bottom: 15px;
                            `,
                        }}
                        value={postalCode}
                        error={error}
                        type="text"
                        maxLength={7}
                        label={translate("Postal Code")}
                        onKeyPress={handleKeyPress}
                        onChange={handleOnChange}
                    />
                    <Button disabled={!!error} onClick={() => getWarehouseData(true)}>
                        {translate("Set My Postal Code")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(PostalCode);
